
import { useEffect, useState } from "react";
import axios from "axios";

const API_BASE_URL = "https://ims.holdthechild.net/api";

const ViewProposals = ({ rfq, onClose }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        // Filter by reference.id instead of rfqId
        const res = await axios.get(`${API_BASE_URL}/vendor-submissions`);
        // Filter client-side for now
        const filteredSubmissions = res.data.filter(
          submission => submission.reference?.id === rfq._id
        );
        setSubmissions(filteredSubmissions);
      } catch (err) {
        console.error("Error fetching submissions:", err);
        alert("Error loading proposals: " + (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, [rfq]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="p-6 max-h-[70vh] overflow-y-auto">
      <h3 className="text-lg font-semibold text-gray-700 mb-4">
        Proposals for {rfq.projectName || "RFQ"}
      </h3>

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          <span className="ml-2">Loading proposals...</span>
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No proposals submitted yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((s) => (
            <div key={s._id} className="border p-4 rounded-lg shadow-sm bg-white hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-semibold text-lg">{s.vendorName}</h4>
                  <p className="text-sm text-gray-600">{s.contactPerson} • {s.contactEmail}</p>
                  {s.vendorCode && <p className="text-xs text-gray-500">Code: {s.vendorCode}</p>}
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  s.status === "submitted"
                    ? "bg-blue-100 text-blue-600"
                    : s.status === "under_review"
                    ? "bg-yellow-100 text-yellow-600"
                    : s.status === "shortlisted"
                    ? "bg-green-100 text-green-600"
                    : s.status === "rejected"
                    ? "bg-red-100 text-red-600"
                    : "bg-gray-100 text-gray-600"
                }`}>
                  {s.status}
                </span>
              </div>

              {s.proposalSummary && (
                <p className="text-gray-700 mb-3 p-2 bg-gray-50 rounded">
                  {s.proposalSummary}
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {s.totalCost && (
                  <p><strong>Total Cost:</strong> {formatCurrency(s.totalCost)}</p>
                )}
                {s.deliveryTimeline && (
                  <p><strong>Delivery:</strong> {s.deliveryTimeline}</p>
                )}
                {s.paymentTerms && (
                  <p><strong>Payment:</strong> {s.paymentTerms}</p>
                )}
                {s.validityPeriod && (
                  <p><strong>Valid Until:</strong> {new Date(s.validityPeriod).toLocaleDateString()}</p>
                )}
              </div>

              {s.attachedDocs && s.attachedDocs.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm font-semibold mb-2">Attachments:</p>
                  <div className="flex flex-wrap gap-2">
                    {s.attachedDocs.map((doc, idx) => (
                      <a
                        key={idx}
                        href={doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-500 hover:text-orange-600 text-sm underline"
                      >
                        📄 {doc.split("/").pop() || `Document ${idx + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                Submitted: {new Date(s.createdAt).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end mt-6">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ViewProposals;